import type { ReactNode, SVGProps } from 'react';

/**
 * Pictogrammes de la vague 7 (Accueil en widgets, aperçu client, agenda, « Nous trouver »), dessinés comme
 * ceux de `src/ui/icons.tsx` (grille de 24, trait de 1,5 px, `currentColor`, décoratifs). Gardés ici en
 * attendant leur reprise dans `src/ui/icons.tsx` (demande de changement L5, vague 7).
 */
type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & { size?: number; strokeWidth?: number };

function make(paths: ReactNode, displayName: string) {
  function Icon({ size = 20, strokeWidth = 1.5, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        focusable="false"
        {...rest}
      >
        {paths}
      </svg>
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

export const ChartIcon = make(<path d="M4.5 19.5h15M7 16v-4M11 16V8M15 16v-6M19 16V5.5" />, 'ChartIcon');
export const ArrowUpIcon = make(<path d="M12 18.5v-13M6.5 11 12 5.5 17.5 11" />, 'ArrowUpIcon');
export const ArrowDownIcon = make(<path d="M12 5.5v13M6.5 13l5.5 5.5 5.5-5.5" />, 'ArrowDownIcon');
export const EyeIcon = make(
  <>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" />
  </>,
  'EyeIcon',
);
export const EyeOffIcon = make(
  <>
    <path d="M4 4l16 16M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a16 16 0 0 1-2.9 3.6M6.4 7.4C3.9 9.1 2.5 12 2.5 12S6 18.5 12 18.5c1.7 0 3.2-.5 4.5-1.2" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </>,
  'EyeOffIcon',
);
export const QrIcon = make(
  <>
    <path d="M4.5 4.5h5v5h-5zM14.5 4.5h5v5h-5zM4.5 14.5h5v5h-5z" />
    <path d="M14.5 14.5h2v2h-2zM18 18h1.5v1.5H18zM14.5 18.5v1M18.5 14.5h1" />
  </>,
  'QrIcon',
);
export const RouteIcon = make(
  <>
    <path d="M12 21s6.5-5.6 6.5-11a6.5 6.5 0 0 0-13 0c0 5.4 6.5 11 6.5 11z" />
    <path d="M9.5 10.5 14.5 8l-2.5 5-.6-1.9z" />
  </>,
  'RouteIcon',
);
export const TimelineIcon = make(
  <>
    <circle cx="6" cy="6.5" r="1.5" />
    <circle cx="6" cy="17.5" r="1.5" />
    <path d="M6 8v8M10 6.5h9.5M10 17.5h9.5M10 12h6" />
  </>,
  'TimelineIcon',
);
export const ListIcon = make(<path d="M8.5 6.5h11M8.5 12h11M8.5 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />, 'ListIcon');

// ─── Base garage (02/10/2026) ───
/** Base garage : répertoire (carnet avec une personne). */
export const BookIcon = make(
  <>
    <path d="M6.5 3.5h11a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1h-11a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2z" />
    <path d="M4.5 17.5a2 2 0 0 1 2-2h12" />
    <circle cx="11.5" cy="8.5" r="2" />
    <path d="M8.5 13c.6-1.3 1.7-2 3-2s2.4.7 3 2" />
  </>,
  'BookIcon',
);
/** Remarque interne (note au coin replié). */
export const NoteIcon = make(
  <>
    <path d="M5.5 4.5h13v10l-5 5h-8z" />
    <path d="M13.5 19.5v-5h5M8.5 9h7M8.5 12h4" />
  </>,
  'NoteIcon',
);
/** Messagerie (02/10/2026) : joindre un fichier (trombone), envoyer, galerie de photos. */
export const PaperclipIcon = make(
  <path d="M19.5 11.5 12 19a4.6 4.6 0 0 1-6.5-6.5l8-8a3.1 3.1 0 0 1 4.4 4.4l-8 8a1.55 1.55 0 0 1-2.2-2.2l7.3-7.3" />,
  'PaperclipIcon',
);
export const SendIcon = make(
  <>
    <path d="M4.5 12 3.5 4.5 20.5 12l-17 7.5z" />
    <path d="M4.5 12h7" />
  </>,
  'SendIcon',
);
export const ImageIcon = make(
  <>
    <rect x="3.5" y="5" width="17" height="14" rx="2" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="m20.5 16-4.5-4.5-8.5 7.5" />
  </>,
  'ImageIcon',
);
